import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import AppButton from '@/components/atoms/AppButton';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { Text } from '@/components/atoms/AppText';
import {
  SparklesIcon,
  SunIcon,
  MoonIcon,
  CalendarIcon,
  CalendarDaysIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import dayjs, { Dayjs } from 'dayjs';
import { getSelectedTimeZone } from '@/utils/dateTime';
import { useTheme } from '@/context/theme.context';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
  APP_GRADIENT,
} from '@/constant/theme/colors.constant';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SHORTCUTS } from './constants';
import { Shortcut } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

enum MODE {
  THIS = 'THIS',
  LAST = 'LAST',
}

const SHORTCUT_LABELS = {
  TODAY: ORGANISM_PROPS.SHORTCUTS_PANEL.TODAY,
  LAST_2_DAYS: ORGANISM_PROPS.SHORTCUTS_PANEL.LAST_2_DAYS,
  WEEK: ORGANISM_PROPS.SHORTCUTS_PANEL.WEEK,
  MONTH: ORGANISM_PROPS.SHORTCUTS_PANEL.MONTH,
  YEAR: ORGANISM_PROPS.SHORTCUTS_PANEL.YEAR,
};

const getShortcutIcon = (label: string) => {
  switch (label) {
    case 'Today':
      return SunIcon;
    case 'Last 2 Days':
      return MoonIcon;
    case 'Week':
      return CalendarIcon;
    case 'Month':
      return CalendarDaysIcon;
    case 'Year':
      return GlobeAltIcon;
    default:
      return CalendarIcon;
  }
};

const getDisplayLabel = (label: string, mode: MODE, standalone: boolean) => {
  if (standalone) return label;

  if (mode === MODE.THIS) {
    return `This ${label}`;
  }

  switch (label) {
    case 'Week':
      return 'Last 7 Days';
    case 'Month':
      return 'Last 30 Days';
    case 'Year':
      return 'Last 365 Days';
    default:
      return `Last ${label}`;
  }
};

interface Props {
  onShortcutSelect: (shortcut: Shortcut) => void;
  selectedShortcut?: string | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

export const ShortcutsPanel: FC<Props> = ({
  onShortcutSelect,
  selectedShortcut,
  startDate,
  endDate,
}) => {
  const [mode, setMode] = useState<MODE>(MODE.LAST);
  const tz = getSelectedTimeZone() || 'UTC';
  const { colors } = useTheme();

  const getMatchedShortcut = useCallback(() => {
    if (!startDate || !endDate) return null;

    const now = dayjs().tz(tz);
    const start = startDate.tz(tz).startOf('day');
    const end = endDate.tz(tz).endOf('day');

    const isSame = (s: any, e: any) =>
      start.isSame(s, 'second') && end.isSame(e, 'second');

    if (isSame(now.startOf('day'), now.endOf('day'))) return 'Today';
    if (isSame(now.subtract(1, 'day').startOf('day'), now.endOf('day')))
      return 'Last 2 Days';
    if (isSame(now.subtract(6, 'day').startOf('day'), now.endOf('day')))
      return 'Last Week';
    if (isSame(now.startOf('week'), now.endOf('day')))
      return 'This Week';
    if (isSame(now.subtract(29, 'day').startOf('day'), now.endOf('day')))
      return 'Last Month';
    if (isSame(now.startOf('month'), now.endOf('day'))) return 'This Month';
    if (isSame(now.subtract(364, 'day').startOf('day'), now.endOf('day')))
      return 'Last Year';
    if (isSame(now.startOf('year'), now.endOf('day'))) return 'This Year';

    return null;
  }, [startDate, endDate, tz]);

  const derived = useMemo(() => getMatchedShortcut(), [getMatchedShortcut]);
  const activeShortcut = selectedShortcut ?? derived;

  useEffect(() => {
    if (!activeShortcut) return;
    if (activeShortcut.startsWith('This') || activeShortcut === 'Today') {
      setMode(MODE.THIS);
    } else {
      setMode(MODE.LAST);
    }
  }, [activeShortcut]);

  const isStandalone = (l: string) =>
    l === SHORTCUT_LABELS.TODAY || l === SHORTCUT_LABELS.LAST_2_DAYS;

  const filtered = SHORTCUTS.filter((s) => {
    if (mode === MODE.THIS && s.label === 'Last 2 Days') return false;
    if (mode === MODE.LAST && s.label === 'Today') return false;
    return true;
  });

  const getPreview = (label: string) => {
    const now = dayjs();
    let start;

    if (label === 'Today') start = now.startOf('day');
    else if (label === 'Last 2 Days') start = now.subtract(1, 'day');
    else if (label === 'Week')
      start =
        mode === MODE.THIS
          ? now.startOf('week')
          : now.subtract(6, 'day');
    else if (label === 'Month')
      start =
        mode === MODE.THIS ? now.startOf('month') : now.subtract(29, 'day');
    else if (label === 'Year')
      start =
        mode === MODE.THIS ? now.startOf('year') : now.subtract(364, 'day');

    return `${start?.format('MMM D')} → ${now.format('MMM D')}`;
  };

  const handleClick = (s: Shortcut) => {
    if (isStandalone(s.label)) return onShortcutSelect(s);

    onShortcutSelect({
      ...s,
      label: `${mode === MODE.THIS ? 'This' : 'Last'} ${s.label}`,
    });
  };

  return (
    <div
      className={`w-full lg:w-60 border-b lg:border-b-0 lg:border-r ${APP_BORDER.default} ${APP_BG.base}`}
    >
      <div className="p-4">
        {/* HEADER */}
        <Text
          as="span"
          size="md"
          weight="bold"
          color="muted"
          tracking="wide"
          uppercase
          className="flex items-center gap-2 mb-3"
        >
          <SparklesIcon className="w-4 h-4 text-primary" />
          {ORGANISM_PROPS.SHORTCUTS_PANEL.QUICK_SELECT}
        </Text>

        {/* MODE SWITCH */}
        <div className="mb-4">
          <SegmentedControl
            testId={tid(TID.SHORTCUTS_MODE_CONTROL)}
            fullWidth
            size="md"
            shape="square"
            variant="gradient"
            options={[
              {
                value: MODE.LAST,
                label: ORGANISM_PROPS.SHORTCUTS_PANEL.MODE_LAST,
              },
              {
                value: MODE.THIS,
                label: ORGANISM_PROPS.SHORTCUTS_PANEL.MODE_THIS,
              },
            ]}
            value={mode}
            onChange={setMode}
          />
        </div>

        {/* LIST */}
        <div className="space-y-1">
          {filtered.map((s, i) => {
            const Icon = getShortcutIcon(s.label);

            const label = isStandalone(s.label)
              ? s.label
              : `${mode === MODE.THIS ? 'This' : 'Last'} ${s.label}`;

            const isActive = activeShortcut === label;

            return (
              <AppButton
                key={s.label}
                data-testid={tid(TID.SHORTCUTS_BTN(s.label))}
                variant="unstyled"
                ripple={false}
                justify="start"
                onClick={() => handleClick(s)}
                style={
                  isActive
                    ? {
                      transitionDelay: `${i * 30}ms`,
                      background: APP_GRADIENT.brand(
                        colors.primary,
                        colors.secondary,
                        colors.tertiary,
                      ),
                    }
                    : { transitionDelay: `${i * 30}ms` }
                }
                className={`
                                    group relative w-full min-h-12 overflow-visible rounded-xl px-3 py-2
                                    transition-all

                                    ${isActive
                    ? 'text-white shadow-md scale-[1.04] border-0'
                    : `${APP_SEMANTIC.neutral.text} ${APP_SEMANTIC.neutral.hover} border-0 `
                  }
                                `}
              >
                {/* LEFT INDICATOR */}
                {isActive && (
                  <span
                    className="absolute -left-1.5 w-1 h-10 rounded-full"
                    style={{
                      background: APP_GRADIENT.primary(
                        colors.primary,
                        colors.secondary,
                      ),
                    }}
                  />
                )}

                {/* ICON + TEXT */}
                <div className="w-full flex items-center gap-3 pr-4">
                  <Icon
                    className={`w-5 h-5 shrink-0 ${isActive ? 'text-white' : APP_SEMANTIC.neutral.text}`}
                  />

                  {/* TEXT */}
                  <div className="flex-1 text-left min-w-0 flex flex-col leading-tight">
                    <Text
                      as="p"
                      size="base"
                      weight="medium"
                      color={isActive ? 'white' : 'soft'}
                      leading="tight"
                      truncate
                    >
                      {getDisplayLabel(s.label, mode, isStandalone(s.label))}
                    </Text>
                    <Text
                      as="p"
                      size="sm"
                      color={isActive ? 'white-soft' : 'subtle'}
                      truncate
                      className="mt-0.5"
                    >
                      {getPreview(s.label)}
                    </Text>
                  </div>
                </div>

                {/* DOT */}
                {isActive && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="h-1.5 w-1.5 bg-white rounded-full" />
                      <div className="absolute inset-0 h-1.5 w-1.5 bg-white rounded-full animate-ping opacity-60" />
                    </div>
                  </div>
                )}
              </AppButton>
            );
          })}
        </div>
      </div>
    </div>
  );
};
